import React, { useState, useEffect, useMemo } from 'react'
import {
  Flex,
  Text,
  Button,
  ProgressCircle,
  Dialog,
  DialogTrigger,
  Content,
  Heading,
  Divider,
  ButtonGroup,
  ActionButton,
  View,
  Image,
  TooltipTrigger,
  Tooltip
} from '@adobe/react-spectrum'
import Download from '@spectrum-icons/workflow/Download'
import Preview from '@spectrum-icons/workflow/Preview'
import AlertIcon from '@spectrum-icons/workflow/Alert'

import { getFileTypeIcon } from '../Theme/Icons/fileTypes'
import {
  getFileCategory,
  createBlobUrl,
  getFileExtension,
  getTruncatedFileName
} from '../util/fileUtils'
import { ALLOWED_FILE_TYPES } from '../constants/fileConstants'

import styles from '../Theme/Styles/styles.css'

// Extend Window interface for global state
declare global {
  interface Window {
    attachmentDialogStates?: Map<string, any>
  }
}

interface AttachmentPreviewProps {
  attachment: any
  chatStatus: string
  darkMode: boolean
  isAgent?: boolean
  onDownload: (attachment: any) => void
  getAttachmentBlob: (attachmentId: string) => any
}

const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({
  attachment,
  chatStatus,
  onDownload,
  getAttachmentBlob,
  isAgent,
  darkMode
}) => {
  const [loading, setLoading] = useState(false)

  // Memoize expensive computations
  const attachmentId = useMemo(() => attachment?.Attachments?.[0]?.AttachmentId, [attachment])
  const attachmentName = useMemo(() => attachment?.Attachments?.[0]?.AttachmentName, [attachment])
  const fileCategory = useMemo(() => getFileCategory(attachmentName), [attachmentName])

  // Global state manager for dialog persistence across component unmounts
  const globalDialogState = useMemo(() => {
    if (!window.attachmentDialogStates) {
      window.attachmentDialogStates = new Map()
    }
    return window.attachmentDialogStates
  }, [])

  const getGlobalState = () => {
    if (!attachmentId) return { showFullPreview: false, previewUrl: null, isViewable: true, csvData: null }
    return globalDialogState.get(attachmentId) || { showFullPreview: false, previewUrl: null, isViewable: true, csvData: null }
  }

  const setGlobalState = (updates: any) => {
    if (!attachmentId) return
    const current = getGlobalState()
    globalDialogState.set(attachmentId, { ...current, ...updates })
  }

  // Initialize state from global state
  const initialState = getGlobalState()
  const [showFullPreview, setShowFullPreview] = useState(initialState.showFullPreview)
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialState.previewUrl)
  const [isViewable, setIsViewable] = useState(initialState.isViewable)
  const [csvData, setCsvData] = useState<{ headers: string[]; rows: string[][] } | null>(initialState.csvData)

  const handleDialogChange = (isOpen: boolean) => {
    setShowFullPreview(isOpen)
    setGlobalState({ showFullPreview: isOpen })
    if (!isOpen) {
      // Clean up when dialog closes
      setGlobalState({ previewUrl: null, isViewable: true, csvData: null })
      setPreviewUrl(null)
      setIsViewable(true)
      setCsvData(null)
    }
  }

  const updatePreviewUrl = (url: string | null) => {
    setPreviewUrl(url)
    setGlobalState({ previewUrl: url })
  }

  const updateIsViewable = (viewable: boolean) => {
    setIsViewable(viewable)
    setGlobalState({ isViewable: viewable })
  }

  const updateCsvData = (data: { headers: string[]; rows: string[][] } | null) => {
    setCsvData(data)
    setGlobalState({ csvData: data })
  }

  // Memoize CSV parsing logic
  const parseCSVLine = useMemo(() => (line: string) => {
    const result = []
    let current = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      const nextChar = line[i + 1]

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"'
          i++ // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes
        }
      } else if (char === ',' && !inQuotes) {
        // Field separator
        result.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }

    // Add the last field
    result.push(current.trim())
    return result
  }, [])

  // CSV parsing
  useEffect(() => {
    if (
      fileCategory === 'text' &&
      getFileExtension(attachmentName) === '.csv' &&
      previewUrl
    ) {
      fetch(previewUrl)
        .then((response) => response.text())
        .then((text) => {
          try {
            // Better CSV parsing that handles quoted fields
            const lines = text.split('\n').filter((line) => line.trim())
            if (lines.length === 0) return

            const headers = parseCSVLine(lines[0])
            const rows = lines.slice(1).map((line) => parseCSVLine(line))
            updateCsvData({ headers, rows })
          } catch (err) {
            console.error('Error parsing CSV:', err)
          }
        })
        .catch((err) => {
          console.error('Error loading CSV:', err)
        })
    }
  }, [previewUrl, fileCategory, attachmentName, parseCSVLine])

  const loadPreview = async () => {
    setLoading(true)
    if (!attachmentId) return

    try {
      const attachmentBlob: any = await getAttachmentBlob(attachmentId)

      if (!attachmentBlob) {
        updatePreviewUrl(null)
        return
      }

      const url = createBlobUrl(attachmentBlob)
      updatePreviewUrl(url)
      updateIsViewable(true)
    } catch (err) {
      console.error('Error loading preview:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = () => {
    onDownload(attachment)
  }

  const handlePreviewClick = async () => {
    if (chatStatus === 'Closed') return

    handleDialogChange(true)

    if (!ALLOWED_FILE_TYPES.includes(fileCategory)) {
      updateIsViewable(false)
      return
    }
    if (!previewUrl) {
      await loadPreview()
    }
  }

  // Memoize theme colors based on participant role
  const agentTheme = useMemo(() => ({
    containerBg: 'var(--spectrum-global-color-static-blue)',
    headerBg: 'rgba(var(--spectrum-global-color-blue-700-rgb), 0.9)',
    contentBg: 'rgba(var(--spectrum-global-color-blue-600-rgb), 0.8)',
    textColor: 'var(--spectrum-global-color-static-white)',
    subtextColor: 'var(--spectrum-global-color-blue-200)',
    iconColor: 'var(--spectrum-global-color-static-white)',
    borderColor: 'var(--spectrum-global-color-blue-800)'
  }), [])

  const customerTheme = useMemo(() => ({
    containerBg: 'gray-50' as const,
    headerBg: 'gray-75' as const,
    contentBg: 'gray-50' as const,
    textColor: 'var(--spectrum-global-color-gray-900)',
    subtextColor: 'var(--spectrum-global-color-gray-600)',
    iconColor: 'var(--spectrum-global-color-gray-600)',
    borderColor: 'dark' as const,
    containerBorderWidth: 'thin' as const
  }), [])

  return (
    <View
      marginTop={'size-160'}
      backgroundColor={isAgent ? undefined : customerTheme.containerBg}
      padding='size-25'
      borderRadius='medium'
      borderWidth={
        !isAgent && darkMode ? customerTheme.containerBorderWidth : undefined
      }
      borderColor={!isAgent && darkMode ? customerTheme.borderColor : undefined}
      UNSAFE_style={{
        flexShrink: 0,
        flexGrow: 0,
        transition: 'all 0.2s ease',
        minWidth: '145px',
        maxWidth: '300px',
        width: 'fit-content',
        ...(isAgent && {
          backgroundColor: agentTheme.containerBg
        })
      }}
    >
      <View
        height='size-600'
        paddingX='size-75'
        paddingY='size-75'
        UNSAFE_style={{
          boxSizing: 'border-box',
          flexShrink: 0,
          width: '100%',
          ...(isAgent && {
            backgroundColor: agentTheme.headerBg
          })
        }}
      >
        <Flex direction='row' alignItems='center' gap='size-50' wrap={false}>
          <View UNSAFE_className={styles['click-to-view-icon']}>
            {getFileTypeIcon(fileCategory, {
              size: 18,
              color: isAgent ? agentTheme.iconColor : customerTheme.iconColor
            })}
          </View>
          <Flex direction='column' flexShrink={1} minWidth={0}>
            <Text
              UNSAFE_style={{
                color: isAgent ? agentTheme.textColor : customerTheme.textColor,
                fontSize: '14px'
              }}
              UNSAFE_className={styles['attachment-name']}
            >
              {getTruncatedFileName(attachmentName)}
            </Text>
            <Text
              UNSAFE_style={{
                color: isAgent ? agentTheme.textColor : customerTheme.textColor,
                fontSize: '10px',
                lineHeight: 1.2,
                margin: 0,
                padding: 0,
                marginTop: '1px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {'Type: '}
              {fileCategory.toUpperCase()}
            </Text>
          </Flex>
          {chatStatus !== 'Closed' && (
            <Flex direction='row' gap='size-50' alignItems='center'>
              <TooltipTrigger delay={100}>
                <ActionButton
                  isQuiet
                  onPress={handlePreviewClick}
                  UNSAFE_style={{
                    flexShrink: 0,
                    minWidth: '20px',
                    height: '20px',
                    padding: 0,
                    borderRadius: '3px',
                    cursor: 'pointer',
                    ...(isAgent && {
                      color: agentTheme.iconColor
                    })
                  }}
                >
                  <Preview size='XS' />
                </ActionButton>
                <Tooltip>Preview</Tooltip>
              </TooltipTrigger>
              <TooltipTrigger delay={100}>
                <ActionButton
                  isQuiet
                  onPress={handleDownload}
                  UNSAFE_style={{
                    flexShrink: 0,
                    minWidth: '20px',
                    height: '20px',
                    padding: 0,
                    borderRadius: '3px',
                    cursor: 'pointer',
                    ...(isAgent && {
                      color: agentTheme.iconColor
                    })
                  }}
                >
                  <Download size='XS' />
                </ActionButton>
                <Tooltip>Download</Tooltip>
              </TooltipTrigger>
            </Flex>
          )}
        </Flex>
      </View>

      <DialogTrigger isOpen={showFullPreview} onOpenChange={handleDialogChange}>
        <View UNSAFE_style={{ display: 'none' }} />
        <Dialog UNSAFE_className={styles['full-preview-dialog']}>
          <Heading
            UNSAFE_style={{
              fontSize: '18px',
              fontWeight: 600,
              color: 'var(--spectrum-global-color-gray-900)',
              padding: '16px 24px 0'
            }}
          >
            {getTruncatedFileName(attachmentName)}
          </Heading>
          <Divider />
          <Content UNSAFE_className={styles['full-preview-content']}>
            {loading && (
              <ProgressCircle aria-label='Loading…' isIndeterminate />
            )}
            {showFullPreview && ((!previewUrl && !csvData && !loading) || !isViewable) && (
              <Flex
                direction='row'
                alignItems='center'
                justifyContent='center'
                gap='size-100'
                UNSAFE_style={{
                  padding: '24px',
                  textAlign: 'center'
                }}
              >
                <AlertIcon
                  size='M'
                  UNSAFE_style={{
                    color: 'var(--spectrum-global-color-orange-600)'
                  }}
                />
                <Text
                  UNSAFE_style={{
                    color: 'var(--spectrum-global-color-gray-700)',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Cannot preview this file. Please download it.
                </Text>
              </Flex>
            )}
            {fileCategory === 'image' && previewUrl && (
              <Image
                src={previewUrl}
                alt={attachmentName || 'Attachment'}
                UNSAFE_className={styles['full-preview-image']}
              />
            )}
            {fileCategory === 'video' && previewUrl && (
              <video
                src={previewUrl}
                controls
                className={styles['full-preview-video']}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  borderRadius: '4px'
                }}
              />
            )}
            {fileCategory === 'audio' && previewUrl && (
              <audio
                src={previewUrl}
                controls
                className={styles['full-preview-audio']}
                style={{
                  width: '100%',
                  borderRadius: '4px'
                }}
              />
            )}
            {fileCategory === 'pdf' && previewUrl && (
              <iframe
                src={previewUrl}
                title={attachmentName}
                className={styles['full-preview-pdf']}
                style={{
                  width: '100%',
                  height: '70vh',
                  border: '1px solid var(--spectrum-global-color-gray-300)',
                  borderRadius: '4px',
                  background: 'var(--spectrum-global-color-gray-50)'
                }}
              />
            )}
            {fileCategory === 'text' &&
              getFileExtension(attachmentName) === '.csv' &&
              csvData && (
                <div
                  style={{
                    width: '100%',
                    height: '70vh',
                    overflow: 'auto',
                    border: `1px solid ${darkMode ? 'var(--spectrum-global-color-gray-600)' : '#e1e1e1'}`,
                    borderRadius: '4px',
                    background: darkMode ? 'var(--spectrum-global-color-gray-800)' : 'white'
                  }}
                >
                  <table 
                    className='csv-table' 
                    style={{ 
                      fontSize: '12px',
                      width: '100%',
                      borderCollapse: 'collapse',
                      color: darkMode ? 'var(--spectrum-global-color-gray-200)' : 'var(--spectrum-global-color-gray-900)'
                    }}
                  >
                    <thead>
                      <tr style={{ 
                        backgroundColor: darkMode ? 'var(--spectrum-global-color-gray-700)' : 'var(--spectrum-global-color-gray-100)'
                      }}>
                        {csvData.headers.map((header, index) => (
                          <th 
                            key={index}
                            style={{
                              padding: '8px 12px',
                              textAlign: 'left',
                              fontWeight: 600,
                              borderBottom: `1px solid ${darkMode ? 'var(--spectrum-global-color-gray-600)' : 'var(--spectrum-global-color-gray-300)'}`,
                              color: darkMode ? 'var(--spectrum-global-color-gray-100)' : 'var(--spectrum-global-color-gray-800)'
                            }}
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvData.rows.map((row, rowIndex) => (
                        <tr 
                          key={rowIndex}
                          style={{
                            backgroundColor: rowIndex % 2 === 0 
                              ? (darkMode ? 'var(--spectrum-global-color-gray-800)' : 'white')
                              : (darkMode ? 'var(--spectrum-global-color-gray-750)' : 'var(--spectrum-global-color-gray-50)')
                          }}
                        >
                          {row.map((cell, cellIndex) => (
                            <td 
                              key={cellIndex}
                              style={{
                                padding: '6px 12px',
                                borderBottom: `1px solid ${darkMode ? 'var(--spectrum-global-color-gray-700)' : 'var(--spectrum-global-color-gray-200)'}`,
                                color: darkMode ? 'var(--spectrum-global-color-gray-300)' : 'var(--spectrum-global-color-gray-800)'
                              }}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

            {fileCategory === 'text' &&
              getFileExtension(attachmentName) !== '.csv' &&
              previewUrl && (
                <iframe
                  src={previewUrl}
                  title={attachmentName}
                  className='full-preview-text'
                  style={{
                    width: '100%',
                    height: '70vh',
                    border: '1px solid #e1e1e1',
                    borderRadius: '4px'
                  }}
                />
              )}
          </Content>
          <ButtonGroup UNSAFE_className='full-preview-button-group'>
            <Button
              variant='primary'
              UNSAFE_className='full-preview-button'
              onPress={() => handleDialogChange(false)}
            >
              Close
            </Button>
          </ButtonGroup>
        </Dialog>
      </DialogTrigger>
          </View>
    )
}

export default AttachmentPreview
